/***************************************************************************
 *
 * Copyright 2025 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { ViewerHotSpotResult } from '@babylonjs/viewer';
import { A3D_TAG_NAMES, A3D_TAGS, A3dElement, toVectorArray as toVectorArrayConverter, } from '../core';
export var A3dHotSpotErrorNames;
(function (A3dHotSpotErrorNames) {
    A3dHotSpotErrorNames["PARENT_WARNING"] = "A3dHotSpotParentWarning";
    A3dHotSpotErrorNames["BAD_VECTOR_INPUT"] = "A3dHotSpotBadVectorInput";
    A3dHotSpotErrorNames["TYPE_SURFACE_MISSING_PROPERTIES"] = "A3dHotSpotTypeSurfaceMissingProperties";
    A3dHotSpotErrorNames["TYPE_WORLD_MISSING_PROPERTIES"] = "A3dHotSpotTypeModelMissingProperties";
})(A3dHotSpotErrorNames || (A3dHotSpotErrorNames = {}));
export const A3dHotSpotErrorMessages = {
    [A3dHotSpotErrorNames.PARENT_WARNING]: `Should be a child of ${A3D_TAGS.VIEWER} or ${A3D_TAGS.MODEL}`,
    [A3dHotSpotErrorNames.BAD_VECTOR_INPUT]: "[A3dHotSpot] Vectors should be defined as 'number number number'",
    [A3dHotSpotErrorNames.TYPE_WORLD_MISSING_PROPERTIES]: "Should have a position and a normal to be valid'",
    [A3dHotSpotErrorNames.TYPE_SURFACE_MISSING_PROPERTIES]: 'Should have a mesh-index, a point-index and a barycentric to be valid',
};
const toVectorArray = (value) => {
    return toVectorArrayConverter(value, A3dHotSpotErrorMessages[A3dHotSpotErrorNames.BAD_VECTOR_INPUT]);
};
let A3dHotSpot = class A3dHotSpot extends A3dElement {
    constructor() {
        super(...arguments);
        /**
         * The fixed world space position of the hot spot.
         * Only work with a `world` hot spot.
         * @attr position - x y z position
         */
        this.position = null;
        /**
         * The fixed world space normal of the hot spot.
         * Only work with a `world` hot spot.
         * @attr normal - x y z normal
         */
        this.normal = null;
        /**
         * The index of the mesh within the parent A3dModel.
         * Only work with a `surface` hot spot.
         * @attr mesh-index
         */
        this.meshIndex = null;
        /**
         * 3 point indices.
         * Only work with a `surface` hot spot.
         * @attr point-index - x y z point index
         */
        this.pointIndex = null;
        /**
         * 3 barycentric coordinates.
         * Only work with a `surface` hot spot.
         * @attr barycentric - x y z barycentric
         */
        this.barycentric = null;
        /**
         * The camera orbit. The camera orbit is optionnal in every type of hot spot.
         * Work with both `surface` and `world` hot spots.
         * @attr camera-orbit - alpha beta radius in radians
         */
        this.cameraOrbit = null;
        this._internals = this.attachInternals();
        this._removeObserver = () => { };
    }
    _createHotSpot() {
        if (this.parentElement?.tagName == A3D_TAG_NAMES.VIEWER) {
            if (this.position && this.normal) {
                return {
                    type: 'world',
                    position: this.position,
                    normal: this.normal,
                    cameraOrbit: this.cameraOrbit ?? undefined,
                };
            }
            else {
                this._warn(A3dHotSpotErrorMessages[A3dHotSpotErrorNames.TYPE_WORLD_MISSING_PROPERTIES]);
            }
        }
        if (this.parentElement?.tagName == A3D_TAG_NAMES.MODEL) {
            if (this.meshIndex && this.pointIndex && this.barycentric) {
                return {
                    type: 'surface',
                    meshIndex: this.meshIndex,
                    pointIndex: this.pointIndex,
                    barycentric: this.barycentric,
                    cameraOrbit: this.cameraOrbit ?? undefined,
                };
            }
            else {
                this._warn(A3dHotSpotErrorMessages[A3dHotSpotErrorNames.TYPE_SURFACE_MISSING_PROPERTIES]);
            }
        }
        return null;
    }
    /**
     * Call the appropriate function to get a hot spot coordinates and visibility.
     * - For 'world' hot spots, it uses the viewer's function.
     * - For 'surface' hot spots, it uses the model's function.
     * @returns The function get a hot spot coordinates and visibility, or null if no valid parent is found.
     */
    _getHotSpotToRef(query, result) {
        if (this.parentElement?.tagName === A3D_TAG_NAMES.MODEL) {
            const model = this.parentElement.model;
            if (model) {
                return model.getHotSpotToRef(query, result);
            }
        }
        if (this.parentElement?.tagName == A3D_TAG_NAMES.VIEWER) {
            return this._viewer?.getWorldHotSpotToRef(query, result) ?? false;
        }
        return false;
    }
    /**
     * Populates the provided result object with the hot spot's coordinates and visibility.
     * @param result The ViewerHotSpotResult object to be populated.
     * @returns True if the hot spot is successfully found and the result is populated, otherwise false.
     */
    getHotSpotToRef(result) {
        const hotSpot = this._createHotSpot();
        if (!hotSpot)
            return false;
        return this._getHotSpotToRef(hotSpot, result);
    }
    _updateHotSpot() {
        if (!this._details) {
            this._internals.states?.add('invalid');
            return;
        }
        // init callback for render camera observable
        const hotSpotResult = new ViewerHotSpotResult();
        const onRenderCamera = () => {
            const isHotSpotFound = this.getHotSpotToRef(hotSpotResult);
            if (isHotSpotFound) {
                const [screenX, screenY] = hotSpotResult.screenPosition;
                this.style.transform = `translate(${screenX}px, ${screenY}px)`;
                this._internals.states?.delete('invalid');
                if (hotSpotResult.visibility <= 0) {
                    this._internals.states?.add('back-facing');
                }
                else {
                    this._internals.states?.delete('back-facing');
                }
            }
            else {
                this._internals.states?.add('invalid');
            }
        };
        // connect hot spot to render camera observable
        this._removeObserver();
        const observer = this._details.scene.onAfterRenderCameraObservable.add(onRenderCamera);
        this._removeObserver = () => {
            if (this._details) {
                this._details.scene.onAfterRenderCameraObservable.remove(observer);
            }
        };
        // call a first onRenderCamera to update the position and the state
        onRenderCamera();
    }
    /**
     * @internal
     */
    async _initElement() {
        this._updateHotSpot();
    }
    connectedCallback() {
        super.connectedCallback();
        this._internals.states?.add('invalid'); // invalid before it is loaded
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this._removeObserver();
    }
    update(changedProperties) {
        super.update(changedProperties);
        this._updateHotSpot();
    }
    /**
     * @internal
     */
    _validateParent() {
        if (this.parentElement?.tagName === A3D_TAG_NAMES.VIEWER ||
            this.parentElement?.tagName === A3D_TAG_NAMES.NODE) {
            return true;
        }
        this._warn(A3dHotSpotErrorMessages[A3dHotSpotErrorNames.PARENT_WARNING]);
        return false;
    }
    render() {
        return html ` <slot></slot> `;
    }
};
A3dHotSpot.styles = css `
    :host {
      display: inline-block;
      position: absolute;
    }

    :host(:state(invalid)) {
      display: none;
    }
  `;
__decorate([
    property({ converter: toVectorArray })
], A3dHotSpot.prototype, "position", void 0);
__decorate([
    property({ converter: toVectorArray })
], A3dHotSpot.prototype, "normal", void 0);
__decorate([
    property({ type: Number, attribute: 'mesh-index' })
], A3dHotSpot.prototype, "meshIndex", void 0);
__decorate([
    property({
        attribute: 'point-index',
        converter: toVectorArray,
    })
], A3dHotSpot.prototype, "pointIndex", void 0);
__decorate([
    property({ converter: toVectorArray })
], A3dHotSpot.prototype, "barycentric", void 0);
__decorate([
    property({
        attribute: 'camera-orbit',
        converter: toVectorArray,
    })
], A3dHotSpot.prototype, "cameraOrbit", void 0);
A3dHotSpot = __decorate([
    customElement('a3d-hot-spot')
], A3dHotSpot);
export { A3dHotSpot };
